// Écran 5 — Fiches de décision (§ 11) : options, raisons pondérées, statut de partage de chaque raison, paramétrage,
// devis, retours d'expérience à 3, 6 et 12 mois.
// - Branché sur l'API : la fiche (contexte, déclencheur, options, raisons et leur partage, décideurs, pertes
//   potentielles évitées, retours d'expérience) est un objet « decision » de la carte, créé puis corrigé par POST
//   /corrections (le champ technique reste `perte_evitee`).
// - Provisoire, le temps de la session seulement (en mémoire, O42) : paramétrage, devis et compléments des retours
//   (rubriques que le schéma pivot v0 ne décrit pas encore). Voir domaine/complementsLocaux.ts.
import { ReserveCalcul } from '../composants/ManquesCalcul';
import { SoumissionDecision } from '../composants/SoumissionCircuit';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';
import type {
  AttributsDecision,
  Carte,
  Lien,
  Objet,
  ObjetConstat,
  ObjetDecision,
  OptionDecision,
  PoidsRaison,
  Raison,
  RetourExperience,
  StatutPartage,
} from '../api/types';
import { Attendre, Erreur, Etiquette } from '../composants/communs';
import { EtapesEcriture, PanneauEcriture, useEcriture } from '../composants/Ecriture';
import { useAuteur, useCarte, useConstats, useEkera } from '../contexte';
import { attributsChanges, correctionModifier, lireSaisie } from '../domaine/corrections';
import {
  ecrireComplements,
  lireComplements,
  montantParUnite,
  type ComplementsDecision,
  type ComplementRetour,
} from '../domaine/complementsLocaux';
import {
  CATEGORIES,
  attributsModifiablesDecision,
  ceQuiSort,
  controler,
  correctionCreerDecision,
  estStructuree,
  etatRetour,
  idDecision,
  instantaneDiagnostic,
  texteCorrections,
  nouvelleRaison,
  partageEffectif,
  preRemplir,
} from '../domaine/decisions';
import { dateFr, eurosCourt, eurosLong, fourchette, LIBELLE_PARTAGE, LIBELLE_POIDS } from '../domaine/format';
import { decideLe } from '../domaine/importation';
import { lienVers, naviguer } from '../routeur';

const aujourdhui = () => new Date().toISOString().slice(0, 10);

export function EcranDecisions({ params }: { params: URLSearchParams }) {
  const carte = useCarte();
  const constats = useConstats();
  return (
    <div className="pile">
      <p className="sous-titre">
        Chaque choix d’architecture a sa fiche : options étudiées, toutes les raisons du choix, pondérées, et pour chacune ce
        qui peut sortir de chez vous. Par défaut, tout est privé ; seules des catégories structurées peuvent être partagées,
        anonymisées, et jamais une fiche individuelle.
      </p>
      <Attendre etats={[carte, constats]} quoi="des décisions">
        {() => <Vue carte={carte.data!} constats={constats.data!.constats} params={params} />}
      </Attendre>
    </div>
  );
}

function Vue({ carte, constats, params }: { carte: Carte; constats: ObjetConstat[]; params: URLSearchParams }) {
  const { ecritureAutorisee } = useEkera();
  const decisions = (carte.objets ?? []).filter((o): o is ObjetDecision => o.type === 'decision' && !o.retire);
  const id = params.get('decision');
  const depuis = params.get('depuis');
  const choisie = decisions.find((d) => d.id === id) ?? null;
  const [constatChoisi, setConstatChoisi] = useState(depuis ?? '');
  const brouillon = useMemo(() => {
    if (id || !depuis) return null;
    const c = constats.find((x) => x.id === depuis);
    return c ? preRemplir(carte, c, aujourdhui()) : null;
  }, [id, depuis, constats, carte]);

  return (
    <>
      <section className="panneau" aria-labelledby="t-liste">
        <div className="panneau-tete">
          <h2 id="t-liste">Décisions enregistrées ({decisions.length})</h2>
        </div>
        {decisions.length === 0 ? (
          <p className="muted">Aucune fiche pour l’instant. Ekera en pré-remplit une à partir d’un constat.</p>
        ) : (
          <div className="tableau-defile">
            <table>
              <thead>
                <tr>
                  <th scope="col">Décision</th>
                  <th scope="col">Option retenue</th>
                  <th scope="col">Décidée le</th>
                  <th scope="col">Retours 3 · 6 · 12 mois</th>
                </tr>
              </thead>
              <tbody>
                {decisions.map((d) => {
                  const a = d.attributs ?? {};
                  return (
                    <tr key={d.id} data-choisi={d.id === id}>
                      <td>
                        <a href={lienVers('decisions', { decision: d.id })}>{d.nom}</a>
                      </td>
                      <td>{a.options?.find((o) => o.retenue)?.libelle ?? '—'}</td>
                      <td>{decideLe(a.decide_le, d.provenance)}</td>
                      <td>
                        {([3, 6, 12] as const).map((m) => (
                          <EtiquetteRetour key={m} etat={etatRetour(a, m, aujourdhui()).etat} mois={m} />
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {ecritureAutorisee ? (
        <form
          className="ligne"
          onSubmit={(e) => {
            e.preventDefault();
            if (constatChoisi) naviguer('decisions', { depuis: constatChoisi });
          }}
        >
          <div className="champ" style={{ flex: '1 1 320px' }}>
            <label htmlFor="d-constat">Nouvelle fiche, pré-remplie depuis un constat</label>
            <select id="d-constat" value={constatChoisi} onChange={(e) => setConstatChoisi(e.target.value)}>
              <option value="">Choisissez un constat…</option>
              {constats.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom}
                </option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn sombre" disabled={!constatChoisi} style={{ alignSelf: 'flex-end' }}>
            Pré-remplir la fiche
          </button>
        </form>
        ) : (
          <p className="note">Lecture seule : votre rôle ne permet pas de créer ni de modifier une fiche de décision.</p>
        )}
      </section>
      {brouillon && <Editeur key={`nouvelle-${depuis}`} initial={brouillon} existante={null} />}
      {choisie && <FicheExistante key={choisie.id} decision={choisie} />}
    </>
  );
}

function EtiquetteRetour({ etat, mois }: { etat: string; mois: number }) {
  const t = { recueilli: ['ok', 'reçu'], a_recueillir: ['grave', 'à relancer'], a_venir: ['neutre', 'à venir'], sans_date: ['neutre', 'non daté'] }[
    etat
  ] as ['ok' | 'grave' | 'neutre', string];
  return (
    <span style={{ marginRight: 4 }}>
      <Etiquette ton={t[0]}>
        {mois} mois : {t[1]}
      </Etiquette>
    </span>
  );
}

function FicheExistante({ decision }: { decision: ObjetDecision }) {
  const { client, cid } = useEkera();
  const lecture = useQuery({
    queryKey: ['carte', cid, 'element', decision.id],
    queryFn: () => client.element(cid, decision.id, 'objet'),
    staleTime: 0,
  });
  if (lecture.error) return <Erreur erreur={lecture.error} />;
  if (!lecture.data) return <p className="chargement">Lecture de la fiche…</p>;
  const lu = lecture.data as { element: ObjetDecision; etag: string };
  return (
    <>
      <SoumissionDecision decision={lu.element.id} />
      <Editeur initial={{ nom: lu.element.nom, attributs: lu.element.attributs ?? {} }} existante={lu} />
      <Retours lu={lu} />
      <Complements decision={lu.element} />
    </>
  );
}

// ---------------------------------------------------------------- éditeur de la fiche

function Editeur({
  initial,
  existante,
}: {
  initial: { nom: string; attributs: AttributsDecision };
  existante: { element: ObjetDecision; etag: string } | null;
}) {
  const auteur = useAuteur();
  const { cid, ecritureAutorisee } = useEkera();
  const qc = useQueryClient();
  const [nom, setNom] = useState(initial.nom);
  const [a, setA] = useState<AttributsDecision>(() => structuredClone(initial.attributs));
  const ecr = useEcriture(existante?.etag, existante?.element ?? null);
  // Lecteur (rôle « lecture ») : fiche affichée, champs verrouillés, aucun bouton d'écriture.
  const verrouille = !ecritureAutorisee || ecr.etat.etape !== 'saisie';
  const options = a.options ?? [];
  const attributsFinal = attributsModifiablesDecision(a);
  const instantane = instantaneDiagnostic(existante?.element);
  const problemes = controler(nom, attributsFinal);
  const bloquants = problemes.filter((p) => p.bloquant);

  const majOption = (i: number, o: Partial<OptionDecision>) =>
    setA({ ...a, options: options.map((x, j) => (j === i ? { ...x, ...o } : x)) });
  const retenir = (i: number) => setA({ ...a, options: options.map((x, j) => ({ ...x, retenue: j === i })) });

  function enregistrer(e: FormEvent) {
    e.preventDefault();
    if (bloquants.length) return;
    if (existante) {
      const avant = (existante.element.attributs ?? {}) as Record<string, unknown>;
      // Les retours d'expérience s'enregistrent à part (plus bas) : la fiche ne les réécrit jamais.
      const { retours_experience: _ignores, ...sansRetours } = attributsFinal;
      const changes = attributsChanges(avant, sansRetours as Record<string, unknown>);
      if (!Object.keys(changes).length && nom === existante.element.nom) return;
      void ecr.simuler(correctionModifier(existante.element, changes, { nom, auteur }));
    } else {
      void ecr.simuler(correctionCreerDecision(nom, attributsFinal, { auteur }));
    }
  }

  return (
    <>
      <section className="panneau" aria-labelledby="t-fiche" data-testid="fiche-decision">
        <div className="panneau-tete">
          <h2 id="t-fiche">{existante ? 'Fiche de décision' : 'Nouvelle fiche, pré-remplie par Ekera'}</h2>
          <span className="indice">
            {existante ? (
              <>
                <span className="num">{existante.element.id}</span> · version lue {existante.etag}
              </>
            ) : (
              <>
                Identifiant : <span className="num">{idDecision(nom || 'sans nom')}</span>
              </>
            )}
          </span>
        </div>
        {instantane && (
          <BandeauDiagnostic instantane={instantane} />
        )}
        <EtapesEcriture etat={ecr.etat} />
        <form className="pile" onSubmit={enregistrer}>
          <fieldset disabled={verrouille} className="pile" style={{ gap: 12 }}>
            <legend>Contexte et déclencheur</legend>
            <div className="champ">
              <label htmlFor="d-nom">Titre</label>
              <input id="d-nom" type="text" value={nom} onChange={(e) => setNom(e.target.value)} />
            </div>
            <div className="champ">
              <label htmlFor="d-contexte">
                Contexte <span className="aide">secteur, taille, sites, criticité, existant</span>
              </label>
              <textarea id="d-contexte" value={a.contexte ?? ''} onChange={(e) => setA({ ...a, contexte: e.target.value })} />
            </div>
            <div className="champ">
              <label htmlFor="d-decl">
                Déclencheur <span className="aide">besoin métier, faille, écart NIS2, audit, exigence d’un donneur d’ordres…</span>
              </label>
              <textarea id="d-decl" value={a.declencheur ?? ''} onChange={(e) => setA({ ...a, declencheur: e.target.value })} />
            </div>
          </fieldset>

          <fieldset disabled={verrouille} className="pile" style={{ gap: 12 }}>
            <legend>Options étudiées</legend>
            <ReserveCalcul manques={instantane?.manques} />
            {options.map((o, i) => (
              <div key={i} className={`option${o.retenue ? ' retenue' : ''}`}>
                <div className="grille g2" style={{ alignItems: 'end' }}>
                  <div className="champ">
                    <label htmlFor={`o-lib-${i}`}>Option {i + 1}</label>
                    <input id={`o-lib-${i}`} type="text" value={o.libelle} onChange={(e) => majOption(i, { libelle: e.target.value })} />
                  </div>
                  <div className="ligne" style={{ alignItems: 'end' }}>
                    <div className="champ" style={{ flex: 1 }}>
                      <label htmlFor={`o-cout-${i}`}>Coût estimé (euros)</label>
                      <input
                        id={`o-cout-${i}`}
                        type="text"
                        inputMode="decimal"
                        value={o.cout?.valeur ?? ''}
                        onChange={(e) => {
                          const t = e.target.value.trim();
                          const v = t ? Number(t.replace(/\s/g, '').replace(',', '.')) : null;
                          majOption(i, { cout: v === null || Number.isNaN(v) ? null : { valeur: v, devise: 'EUR', periode: 'unique' } });
                        }}
                      />
                    </div>
                    <label className="case" style={{ paddingBottom: 8 }}>
                      <input type="radio" name="retenue" checked={!!o.retenue} onChange={() => retenir(i)} />
                      Retenue
                    </label>
                  </div>
                </div>
                <Raisons
                  titre={o.retenue ? 'Raisons du choix' : 'Raisons en faveur'}
                  prefixe={`o${i}-r`}
                  raisons={o.raisons ?? []}
                  surChange={(r) => majOption(i, { raisons: r })}
                />
                {!o.retenue && (
                  <Raisons
                    titre="Ce qui l’a éliminée"
                    prefixe={`o${i}-x`}
                    raisons={o.raisons_rejet ?? []}
                    surChange={(r) => majOption(i, { raisons_rejet: r })}
                  />
                )}
              </div>
            ))}
            <div>
              <button
                type="button"
                className="btn petit"
                onClick={() => setA({ ...a, options: [...options, { libelle: '', retenue: false, raisons: [], raisons_rejet: [] }] })}
              >
                Ajouter une option
              </button>
            </div>
          </fieldset>

          <ReserveCalcul manques={instantane?.manques} />
          <fieldset disabled={verrouille} className="grille g3">
            <legend>Décideurs et effets estimés</legend>
            <div className="champ">
              <span>Décideurs</span>
              <span>{a.decideurs?.join(', ') || 'Non désignés'}</span>
            </div>
            <div className="champ">
              <span>Décidée le</span>
              <span>{a.decide_le ? decideLe(a.decide_le, existante?.element.provenance) : 'Non datée'}</span>
            </div>
            <div className="champ">
              <span className="muted" style={{ fontSize: 13.5, fontWeight: 600 }}>
                Pertes potentielles évitées
              </span>
              <span>{a.perte_evitee ? fourchette(a.perte_evitee.bas.valeur, a.perte_evitee.haut.valeur) : 'non estimée'}</span>
            </div>
          </fieldset>
          <p className="note">Les décideurs et la date de décision sont établis à la fin du circuit de validation.</p>

          <CeQuiSort options={options} />

          {problemes.length > 0 && (
            <div className={`encadre ${bloquants.length ? 'erreur' : 'attention'}`} role="status">
              <p>
                <b>{bloquants.length ? 'À compléter avant d’enregistrer :' : 'Points d’attention :'}</b>
              </p>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {problemes.map((p, i) => (
                  <li key={i}>{p.message}</li>
                ))}
              </ul>
            </div>
          )}
          {!verrouille && (
            <div className="ligne">
              <button type="submit" className="btn sombre" disabled={bloquants.length > 0 || ecr.occupe}>
                Voir la simulation
              </button>
            </div>
          )}
        </form>
      </section>
      <PanneauEcriture
        ecr={ecr}
        libelleValider={existante ? 'Enregistrer la fiche' : 'Créer la fiche'}
        surReprendre={(actuel) => {
          qc.setQueryData(['carte', cid, 'element', actuel.element.id], actuel);
          ecr.reinitialiser();
        }}
      />
      {ecr.etat.etape === 'ecrite' && !existante && (
        <p>
          <a className="btn" href={lienVers('decisions', { decision: ecr.etat.resultat.reponse.element })}>
            Ouvrir la fiche enregistrée
          </a>
        </p>
      )}
    </>
  );
}

/** Statut de partage proposable : sans le rôle « partage », on peut garder le statut actuel ou fermer (« privé »),
 * jamais ouvrir ni changer un partage (O18 ; l'API refuse de toute façon avec 403 role_partage). */
export function partageProposable(v: StatutPartage, actuel: StatutPartage, peutPartager: boolean): boolean {
  return peutPartager || v === 'prive' || v === actuel;
}

function Raisons({
  titre,
  prefixe,
  raisons,
  surChange,
}: {
  titre: string;
  prefixe: string;
  raisons: Raison[];
  surChange: (r: Raison[]) => void;
}) {
  const maj = (i: number, r: Partial<Raison>) => surChange(raisons.map((x, j) => (j === i ? { ...x, ...r } : x)));
  // Ouvrir ou modifier un partage hors de l'entreprise exige le rôle dédié « partage » (O18) ; le fermer (« privé »)
  // reste permis. L'API refuse de toute façon (403 role_partage).
  const { peutPartager } = useEkera();
  return (
    <div>
      <h3 style={{ fontSize: 14 }}>{titre}</h3>
      {raisons.length === 0 && <p className="note">Aucune raison.</p>}
      {raisons.map((r, i) => {
        const structuree = estStructuree(r.categorie);
        const id = `${prefixe}${i}`;
        return (
          <div className="raison" key={i}>
            <div className="champ">
              <label htmlFor={`${id}-t`}>Raison</label>
              <input id={`${id}-t`} type="text" value={r.texte} onChange={(e) => maj(i, { texte: e.target.value })} />
            </div>
            <div className="champ">
              <label htmlFor={`${id}-c`}>Catégorie</label>
              <select
                id={`${id}-c`}
                value={r.categorie ?? ''}
                onChange={(e) => {
                  const c = e.target.value || null;
                  maj(i, { categorie: c, partage: estStructuree(c) ? r.partage : 'prive' });
                }}
              >
                <option value="">Texte libre (privé)</option>
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.libelle}
                  </option>
                ))}
              </select>
            </div>
            <div className="champ">
              <label htmlFor={`${id}-p`}>Poids</label>
              <select id={`${id}-p`} value={r.poids ?? 'importante'} onChange={(e) => maj(i, { poids: e.target.value as PoidsRaison })}>
                {Object.entries(LIBELLE_POIDS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div className="champ">
              <label htmlFor={`${id}-s`}>Partage</label>
              <select
                id={`${id}-s`}
                value={partageEffectif(r)}
                disabled={!structuree}
                aria-describedby={!structuree ? `${id}-sa` : undefined}
                onChange={(e) => maj(i, { partage: e.target.value as StatutPartage })}
              >
                {Object.entries(LIBELLE_PARTAGE).map(([v, l]) => (
                  <option key={v} value={v} disabled={!partageProposable(v as StatutPartage, partageEffectif(r), peutPartager)}>
                    {l}
                  </option>
                ))}
              </select>
              {structuree && !peutPartager && (
                <span className="aide">Partager hors de l’entreprise exige le rôle « partage ».</span>
              )}
              {!structuree && (
                <span id={`${id}-sa`} className="aide">
                  Privé : choisissez une catégorie pour pouvoir la partager.
                </span>
              )}
            </div>
            <button type="button" className="btn petit" aria-label={`Ôter la raison ${i + 1} de la fiche`} onClick={() => surChange(raisons.filter((_, j) => j !== i))}>
              Ôter
            </button>
          </div>
        );
      })}
      <button type="button" className="btn petit" style={{ marginTop: 8 }} onClick={() => surChange([...raisons, nouvelleRaison()])}>
        Ajouter une raison
      </button>
    </div>
  );
}

function CeQuiSort({ options }: { options: OptionDecision[] }) {
  const lignes = options.flatMap((o) =>
    [...(o.raisons ?? []), ...(o.raisons_rejet ?? [])].map((r) => ({ option: o.libelle, r, sortie: ceQuiSort(r) })),
  );
  if (!lignes.length) return null;
  return (
    <section className="encadre info pile sortie" aria-labelledby="t-sortie" style={{ gap: 6 }}>
      <h3 id="t-sortie">Ce qui sortirait de chez vous (avec votre accord écrit)</h3>
      <p>
        Seulement dans des agrégats d’au moins cinq entreprises, jamais une fiche individuelle ; les failles ne sortent jamais.
      </p>
      <div className="tableau-defile">
        <table>
          <thead>
            <tr>
              <th scope="col">Raison (reste chez vous)</th>
              <th scope="col">Statut</th>
              <th scope="col">Ce qui sort</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map(({ option, r, sortie }, i) => (
              <tr key={i}>
                <td>
                  {r.texte || <i>sans texte</i>} <span className="muted">({option || 'option sans libellé'})</span>
                </td>
                <td>{LIBELLE_PARTAGE[partageEffectif(r)]}</td>
                <td>{sortie ? `${sortie.categorie}${sortie.poids ? `, ${LIBELLE_POIDS[sortie.poids as PoidsRaison].toLowerCase()}` : ''}` : 'Rien'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- retours d'expérience (API) + compléments (local)

function Retours({ lu }: { lu: { element: ObjetDecision; etag: string } }) {
  const a = lu.element.attributs ?? {};
  return (
    <section className="panneau" aria-labelledby="t-retex">
      <div className="panneau-tete">
        <h2 id="t-retex">Retours d’expérience à 3, 6 et 12 mois</h2>
        <span className="indice">{a.decide_le ? `Décidée le ${decideLe(a.decide_le, lu.element.provenance)}` : 'La date de décision sera établie à la fin du circuit de validation.'}</span>
      </div>
      <div className="grille g3">
        {([3, 6, 12] as const).map((m) => (
          <Retour key={m} lu={lu} mois={m} />
        ))}
      </div>
    </section>
  );
}

function Retour({ lu, mois }: { lu: { element: ObjetDecision; etag: string }; mois: 3 | 6 | 12 }) {
  const auteur = useAuteur();
  const { cid, ecritureAutorisee } = useEkera();
  const qc = useQueryClient();
  const a = lu.element.attributs ?? {};
  const { etat, date, retour } = etatRetour(a, mois, aujourdhui());
  const [cout, setCout] = useState(retour?.cout_reel?.valeur?.toString() ?? '');
  const [delai, setDelai] = useState(retour?.delai_reel_jours?.toString() ?? '');
  const [difficultes, setDifficultes] = useState(retour?.difficultes ?? '');
  const [referiez, setReferiez] = useState<string>(retour?.referiez_vous === true ? 'oui' : retour?.referiez_vous === false ? 'non' : '');
  const [erreur, setErreur] = useState<string | null>(null);
  const [local, setLocal] = useState<ComplementsDecision>(() => lireComplements(cid, lu.element.id));
  const comp: ComplementRetour = local.retours[String(mois) as '3'] ?? {};
  const [localOk, setLocalOk] = useState<boolean | null>(null);
  const ecr = useEcriture(lu.etag, lu.element);

  function envoyer(e: FormEvent) {
    e.preventDefault();
    setErreur(null);
    try {
      const r: RetourExperience = { a_mois: mois };
      if (cout.trim()) r.cout_reel = { valeur: lireSaisie('nombre', cout) as number, devise: 'EUR', periode: 'unique' };
      if (delai.trim()) r.delai_reel_jours = Math.round(lireSaisie('nombre', delai) as number);
      if (difficultes.trim()) r.difficultes = difficultes.trim();
      if (referiez) r.referiez_vous = referiez === 'oui';
      const autres = (a.retours_experience ?? []).filter((x) => x.a_mois !== mois);
      const liste = [...autres, r].sort((x, y) => x.a_mois - y.a_mois);
      void ecr.simuler(correctionModifier(lu.element, { retours_experience: liste }, { auteur, motif: `retour d’expérience à ${mois} mois` }));
    } catch (err) {
      setErreur(err instanceof Error ? err.message : String(err));
    }
  }

  const majLocal = (c: ComplementRetour) => {
    const n = { ...local, retours: { ...local.retours, [String(mois)]: { ...comp, ...c } } };
    setLocal(n);
    setLocalOk(ecrireComplements(cid, lu.element.id, n));
  };
  // Lecteur (rôle « lecture ») : fiche affichée, champs verrouillés, aucun bouton d'écriture.
  const verrouille = !ecritureAutorisee || ecr.etat.etape !== 'saisie';
  return (
    <div className="option">
      <div className="panneau-tete">
        <h3>À {mois} mois</h3>
        <EtiquetteRetour etat={etat} mois={mois} />
      </div>
      {date && <p className="note">Échéance : {dateFr(date)}</p>}
      <form className="pile" style={{ gap: 8 }} onSubmit={envoyer}>
        <fieldset disabled={verrouille} className="pile" style={{ gap: 8, border: 0, padding: 0 }}>
          <legend className="sr">Retour à {mois} mois, enregistré dans la carte</legend>
          <div className="champ">
            <label htmlFor={`rx${mois}-c`}>Coût réel (euros)</label>
            <input id={`rx${mois}-c`} type="text" inputMode="decimal" value={cout} onChange={(e) => setCout(e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor={`rx${mois}-d`}>Délai réel (jours)</label>
            <input id={`rx${mois}-d`} type="text" inputMode="numeric" value={delai} onChange={(e) => setDelai(e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor={`rx${mois}-f`}>Difficultés, écarts au plan</label>
            <textarea id={`rx${mois}-f`} value={difficultes} onChange={(e) => setDifficultes(e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor={`rx${mois}-r`}>Referiez-vous ce choix ?</label>
            <select id={`rx${mois}-r`} value={referiez} onChange={(e) => setReferiez(e.target.value)}>
              <option value="">Sans réponse</option>
              <option value="oui">Oui</option>
              <option value="non">Non</option>
            </select>
          </div>
        </fieldset>
        {erreur && (
          <p className="encadre erreur" role="alert">
            {erreur}
          </p>
        )}
        {!verrouille && (
          <button type="submit" className="btn petit sombre">
            Voir la simulation
          </button>
        )}
      </form>
      <PanneauEcriture
        ecr={ecr}
        libelleValider="Enregistrer le retour"
        surReprendre={(actuel) => {
          qc.setQueryData(['carte', cid, 'element', actuel.element.id], actuel);
          ecr.reinitialiser();
        }}
      />
      <div className="encadre provisoire pile" style={{ gap: 8 }}>
        <p className="note">
          <b>Le temps de cette session seulement</b> (provisoire : l’API v0 ne porte pas ces rubriques ; effacé à la
          déconnexion et au rechargement de la page)
        </p>
        <div className="champ">
          <label htmlFor={`rx${mois}-fa`}>Facture (euros)</label>
          <input
            id={`rx${mois}-fa`}
            type="text"
            inputMode="decimal"
            value={comp.facture ?? ''}
            onChange={(e) => {
              const v = Number(e.target.value.replace(/\s/g, '').replace(',', '.'));
              majLocal({ facture: e.target.value.trim() && !Number.isNaN(v) ? v : null });
            }}
          />
        </div>
        <div className="champ">
          <label htmlFor={`rx${mois}-i`}>Incidents survenus ou évités</label>
          <textarea id={`rx${mois}-i`} value={comp.incidents ?? ''} onChange={(e) => majLocal({ incidents: e.target.value })} />
        </div>
        <div className="champ">
          <label htmlFor={`rx${mois}-dv`}>Paramétrage en place et dérives</label>
          <textarea id={`rx${mois}-dv`} value={comp.derives ?? ''} onChange={(e) => majLocal({ derives: e.target.value })} />
        </div>
        {localOk === false && <p className="note" role="alert">Le complément n’a pas pu être gardé.</p>}
      </div>
    </div>
  );
}

function Complements({ decision }: { decision: Objet | Lien }) {
  const { cid } = useEkera();
  const [c, setC] = useState<ComplementsDecision>(() => lireComplements(cid, decision.id));
  const [ok, setOk] = useState<boolean | null>(null);
  const maj = (n: ComplementsDecision) => {
    setC(n);
    setOk(ecrireComplements(cid, decision.id, n));
  };
  return (
    <section className="panneau encadre provisoire" aria-labelledby="t-param" data-testid="complements-locaux">
      <div className="panneau-tete">
        <h2 id="t-param">Mise en œuvre, paramétrage et devis</h2>
        <Etiquette ton="attention">Provisoire · gardé le temps de la session seulement</Etiquette>
      </div>
      <p className="note">
        Le schéma pivot v0 ne décrit pas encore le paramétrage ni les devis : ces rubriques restent dans la mémoire de cet
        onglet, le temps de votre session. Elles sont effacées à la déconnexion et au rechargement de la page, ne sont
        écrites ni sur le disque du poste, ni dans l’API, et ne sont ni partagées, ni sauvegardées, ni journalisées.
        {ok === false && ' Le complément n’a pas pu être gardé.'}
      </p>
      <h3>Règles de paramétrage décidées</h3>
      {c.parametrage.map((r, i) => {
        const majR = (x: Partial<typeof r>) => maj({ ...c, parametrage: c.parametrage.map((y, j) => (j === i ? { ...y, ...x } : y)) });
        return (
          <div key={i} className="option">
            <div className="champ">
              <label htmlFor={`pr-${i}`}>Règle</label>
              <input id={`pr-${i}`} type="text" value={r.regle} onChange={(e) => majR({ regle: e.target.value })} />
            </div>
            <label className="case">
              <input type="checkbox" checked={r.appliquee} onChange={(e) => majR({ appliquee: e.target.checked })} />
              Appliquée telle que proposée
            </label>
            {!r.appliquee && (
              <div className="grille g3">
                <div className="champ">
                  <label htmlFor={`pj-${i}`}>Justification de l’écart</label>
                  <input
                    id={`pj-${i}`}
                    type="text"
                    value={r.ecart?.justification ?? ''}
                    onChange={(e) => majR({ ecart: { accepte_par: '', jusqu_au: '', ...r.ecart, justification: e.target.value } })}
                  />
                </div>
                <div className="champ">
                  <label htmlFor={`pa-${i}`}>Accepté par</label>
                  <input
                    id={`pa-${i}`}
                    type="text"
                    value={r.ecart?.accepte_par ?? ''}
                    onChange={(e) => majR({ ecart: { justification: '', jusqu_au: '', ...r.ecart, accepte_par: e.target.value } })}
                  />
                </div>
                <div className="champ">
                  <label htmlFor={`pu-${i}`}>Jusqu’au</label>
                  <input
                    id={`pu-${i}`}
                    type="date"
                    value={r.ecart?.jusqu_au ?? ''}
                    onChange={(e) => majR({ ecart: { justification: '', accepte_par: '', ...r.ecart, jusqu_au: e.target.value } })}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div>
        <button type="button" className="btn petit" onClick={() => maj({ ...c, parametrage: [...c.parametrage, { regle: '', appliquee: true }] })}>
          Ajouter une règle
        </button>
      </div>
      <h3>Devis, ramenés à l’unité</h3>
      <div className="tableau-defile">
        <table>
          <thead>
            <tr>
              <th scope="col">Fournisseur</th>
              <th scope="col" className="num">
                Montant
              </th>
              <th scope="col" className="num">
                Quantité
              </th>
              <th scope="col">Unité</th>
              <th scope="col">Date</th>
              <th scope="col" className="num">
                Par unité
              </th>
            </tr>
          </thead>
          <tbody>
            {c.devis.map((d, i) => {
              const majD = (x: Partial<typeof d>) => maj({ ...c, devis: c.devis.map((y, j) => (j === i ? { ...y, ...x } : y)) });
              const pu = montantParUnite(d);
              return (
                <tr key={i}>
                  <td>
                    <input aria-label={`Fournisseur du devis ${i + 1}`} type="text" value={d.fournisseur} onChange={(e) => majD({ fournisseur: e.target.value })} />
                  </td>
                  <td>
                    <input
                      aria-label={`Montant du devis ${i + 1}`}
                      type="number"
                      min="0"
                      value={d.montant || ''}
                      onChange={(e) => majD({ montant: Number(e.target.value) })}
                    />
                  </td>
                  <td>
                    <input
                      aria-label={`Quantité du devis ${i + 1}`}
                      type="number"
                      min="0"
                      value={d.quantite || ''}
                      onChange={(e) => majD({ quantite: Number(e.target.value) })}
                    />
                  </td>
                  <td>
                    <input aria-label={`Unité du devis ${i + 1}`} type="text" value={d.unite} placeholder="utilisateur, serveur…" onChange={(e) => majD({ unite: e.target.value })} />
                  </td>
                  <td>
                    <input aria-label={`Date du devis ${i + 1}`} type="date" value={d.date} onChange={(e) => majD({ date: e.target.value })} />
                  </td>
                  <td className="num">{pu !== null ? `${eurosLong(pu)} / ${d.unite || 'unité'}` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="ligne">
        <button
          type="button"
          className="btn petit"
          onClick={() => maj({ ...c, devis: [...c.devis, { fournisseur: '', montant: 0, quantite: 1, unite: '', date: aujourdhui() }] })}
        >
          Ajouter un devis
        </button>
        {c.devis.length > 0 && <span className="note">Total : {eurosCourt(c.devis.reduce((s, d) => s + (d.montant || 0), 0))}</span>}
      </div>
    </section>
  );
}

/** Bandeau « Chiffres du diagnostic » : instantané d'Ekera, ou fiche corrigée depuis par le DSI (P12). */
export function BandeauDiagnostic({ instantane }: { instantane: NonNullable<ReturnType<typeof instantaneDiagnostic>> }) {
  const date = instantane.date ? ` du ${dateFr(instantane.date)}` : '';
  const corriges = texteCorrections(instantane.corrections);
  return (
    <div>
      <ReserveCalcul manques={instantane.manques} />
    <p className="note" data-testid="fiche-instantane">
      Chiffres du diagnostic{date} :{' '}
      {corriges ? (
        <>
          fiche pré-remplie par Ekera, éléments {corriges} ; les euros qu’elle porte ne sont donc plus ceux calculés par Ekera à
          cette date, et ne suivent pas non plus les autres corrections de la carte.
        </>
      ) : (
        <>les euros pré-remplis par Ekera ne suivent pas les corrections faites depuis sur la carte.</>
      )}{' '}
      Pour les chiffres actuels, voir les <a href={lienVers('tableau')}>scénarios d’investissement proposés du tableau de bord</a>, recalculés
      par le moteur à chaque lecture.
    </p>
    </div>
  );
}
